import React, { useEffect, useRef, useState } from 'react'
import { Form, Modal } from 'semantic-ui-react'
import styled from 'styled-components'
import { X } from 'phosphor-react'
import { FlexRowBetween, TitleText } from 'components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import {
    NAME_FORMAT_TIMEZONE_OPTIONS,
    buildFormattedName,
    buildPdfFilenamePreview,
    hasAtLeastOnePart,
    toApiPayload,
    validateOtherText,
    withCompanyTimezone,
} from 'utility'

const Section = styled.div`
    padding: 14px 0;
    border-bottom: 1px solid #f3f4f6;
    &:last-of-type {
        border-bottom: none;
    }
`

const SectionTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin-bottom: 8px;
`

const ConditionalBlock = styled.div`
    margin-top: 10px;
    padding-left: 24px;
    border-left: 2px solid #e5e7eb;
    display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
`

const PreviewBox = styled.div`
    margin-top: 16px;
    padding: 14px 16px;
    background: ${({ $variant }) =>
        $variant === 'pdf'
            ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
            : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)'};
    border: 1px solid
        ${({ $variant }) => ($variant === 'pdf' ? '#fbbf24' : '#bae6fd')};
    border-radius: 8px;
`

const PreviewLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${({ $variant }) => ($variant === 'pdf' ? '#92400e' : '#0369a1')};
    letter-spacing: 0.05em;
`

const PreviewValue = styled.div`
    margin-top: 6px;
    font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 14px;
    color: ${({ $variant }) => ($variant === 'pdf' ? '#78350f' : '#0c4a6e')};
    word-break: break-all;
`

const PreviewEmpty = styled.div`
    margin-top: 6px;
    font-style: italic;
    color: #64748b;
    font-size: 13px;
`

const CharHelp = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-top: 6px;
    code {
        background: #f3f4f6;
        padding: 1px 5px;
        border-radius: 3px;
        font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
        font-size: 11px;
    }
`

const CharError = styled.div`
    margin-top: 6px;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 12px;
    display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};
`

const PreviewNote = styled.div`
    margin-top: 8px;
    font-size: 11px;
    color: #92400e;
`

const HintText = styled.p`
    margin-top: 0;
    color: #6b7280;
    font-size: 13px;
`

const UserHint = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-top: 6px;
    padding-left: 24px;
`

export const NameFormatModal = ({
    open,
    onClose,
    onSave,
    loading,
    saveLabel = 'Save',
    variant = 'inspection',
    initialConfig,
    sample,
    companyTimeZone,
}) => {
    const [titleSource, setTitleSource] = useState('template')
    const [otherText, setOtherText] = useState('')
    const [includeDateTime, setIncludeDateTime] = useState(false)
    const [timezone, setTimezone] = useState('Pacific Standard Time')
    const [includeUsername, setIncludeUsername] = useState(false)
    const [otherError, setOtherError] = useState('')
    const [otherInvalid, setOtherInvalid] = useState(false)

    const isPdf = variant === 'pdf'
    const title = isPdf ? 'PDF File Name Format' : 'Inspection Name Format'
    // Inspection names use the company time zone when it is set (server rule).
    const usesCompanyTimeZone = !isPdf && Boolean(companyTimeZone)
    const wasOpenRef = useRef(false)

    useEffect(() => {
        if (open && !wasOpenRef.current && initialConfig) {
            setTitleSource(initialConfig.titleSource || 'template')
            setOtherText(initialConfig.otherText || '')
            setIncludeDateTime(Boolean(initialConfig.includeDateTime))
            setTimezone(initialConfig.timezone || 'Pacific Standard Time')
            setIncludeUsername(Boolean(initialConfig.includeUsername))
            setOtherError('')
            setOtherInvalid(false)
        }
        wasOpenRef.current = open
    }, [open, initialConfig])

    const cfg = {
        titleSource,
        otherText,
        includeDateTime,
        timezone,
        includeUsername,
    }

    const validation = validateOtherText(otherText, titleSource === 'other')
    const canSave =
        validation.valid &&
        hasAtLeastOnePart(cfg) &&
        (!includeDateTime || Boolean(timezone))

    const preview = isPdf
        ? buildPdfFilenamePreview(cfg, sample)
        : buildFormattedName(
              usesCompanyTimeZone ? withCompanyTimezone(cfg, companyTimeZone) : cfg,
              sample,
          )

    const handleOtherTextChange = e => {
        const val = e.target.value
        setOtherText(val)
        const result = validateOtherText(val, titleSource === 'other')
        setOtherError(result.message)
        setOtherInvalid(!result.valid)
    }

    const handleTitleChange = (_, { value }) => {
        setTitleSource(value)
        const result = validateOtherText(otherText, value === 'other')
        setOtherError(result.message)
        setOtherInvalid(!result.valid)
    }

    const handleSave = () => {
        const result = validateOtherText(otherText, titleSource === 'other')
        if (!result.valid || !hasAtLeastOnePart(cfg)) return
        onSave(toApiPayload(cfg))
    }

    return (
        <Modal open={open} onClose={onClose} size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>{title}</TitleText>
                    <X className="pointer" size={32} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content style={{ maxHeight: '75vh', overflow: 'auto' }}>
                <HintText>
                    {isPdf
                        ? "Pick the parts that make up the PDF file name. At least one part must be selected. The server adds .pdf and a unique _a<auditId> suffix automatically."
                        : 'Pick the parts that make up the inspection name when a user starts a new inspection from a template. At least one part must be selected.'}
                </HintText>

                <Section>
                    <SectionTitle>1. Title</SectionTitle>
                    <Form>
                        <Form.Field>
                            <Form.Radio
                                label={
                                    <label>
                                        Use <strong>Template name</strong> — e.g.{' '}
                                        <em>{sample.templateName}</em>
                                    </label>
                                }
                                name="titleSource"
                                value="template"
                                checked={titleSource === 'template'}
                                onChange={handleTitleChange}
                            />
                            <Form.Radio
                                label={
                                    <label>
                                        Use <strong>Company name</strong> — e.g.{' '}
                                        <em>{sample.companyName}</em>
                                    </label>
                                }
                                name="titleSource"
                                value="company"
                                checked={titleSource === 'company'}
                                onChange={handleTitleChange}
                            />
                            <Form.Radio
                                label={
                                    <label>
                                        <strong>Other</strong> — type your own
                                    </label>
                                }
                                name="titleSource"
                                value="other"
                                checked={titleSource === 'other'}
                                onChange={handleTitleChange}
                            />
                            <Form.Radio
                                label="None — skip the title segment"
                                name="titleSource"
                                value="none"
                                checked={titleSource === 'none'}
                                onChange={handleTitleChange}
                            />
                        </Form.Field>
                    </Form>
                    <ConditionalBlock $hidden={titleSource !== 'other'}>
                        <label
                            style={{
                                display: 'block',
                                fontSize: 13,
                                fontWeight: 500,
                                marginBottom: 6,
                            }}
                        >
                            Custom title text
                        </label>
                        <Form.Input
                            value={otherText}
                            onChange={handleOtherTextChange}
                            maxLength={50}
                            placeholder="e.g. Daily Walkthrough"
                            error={otherInvalid}
                        />
                        <CharHelp>
                            Max 50 characters. Not allowed:{' '}
                            <code>&lt; &gt; : &quot; / \ | ? *</code> and control
                            characters.
                        </CharHelp>
                        <CharError $hidden={!otherError}>{otherError}</CharError>
                    </ConditionalBlock>
                </Section>

                <Section>
                    <SectionTitle>2. Date &amp; Time</SectionTitle>
                    <Form.Checkbox
                        label={
                            isPdf
                                ? 'Include date and time (when the PDF was generated)'
                                : 'Include date and time'
                        }
                        checked={includeDateTime}
                        onChange={(_, { checked }) =>
                            setIncludeDateTime(checked)
                        }
                    />
                    <ConditionalBlock $hidden={!includeDateTime}>
                        <label
                            style={{
                                display: 'block',
                                fontSize: 13,
                                fontWeight: 500,
                                marginBottom: 6,
                                marginTop: 8,
                            }}
                        >
                            Time zone
                        </label>
                        {usesCompanyTimeZone ? (
                            <CharHelp>
                                Company time zone: <strong>{companyTimeZone}</strong>{' '}
                                (TimeZone on the company settings). Daylight saving is applied.
                            </CharHelp>
                        ) : (
                            <>
                                <Form.Select
                                    options={NAME_FORMAT_TIMEZONE_OPTIONS}
                                    value={timezone}
                                    onChange={(_, { value }) => setTimezone(value)}
                                />
                                <CharHelp>
                                    Format:{' '}
                                    <code>yyyy-MM-dd HH:mm zzz</code>
                                    {isPdf && '. DST is auto-applied (e.g. EST ↔ EDT).'}
                                </CharHelp>
                            </>
                        )}
                    </ConditionalBlock>
                </Section>

                <Section>
                    <SectionTitle>3. User</SectionTitle>
                    <Form.Checkbox
                        label={
                            isPdf
                                ? "Include the PDF-generator's name (whoever clicked Download)"
                                : "Include user's name (the person starting the inspection)"
                        }
                        checked={includeUsername}
                        onChange={(_, { checked }) =>
                            setIncludeUsername(checked)
                        }
                    />
                    <UserHint>
                        Uses <strong>FullName</strong> (whitespace removed, max 10
                        chars). If FullName is empty, falls back to UserId.
                        {isPdf && (
                            <>
                                <br />
                                This is the user who <em>generated</em> the PDF, not
                                the audit owner.
                            </>
                        )}
                    </UserHint>
                </Section>

                <PreviewBox $variant={variant}>
                    <PreviewLabel $variant={variant}>
                        {isPdf ? 'Preview filename' : 'Preview'}
                    </PreviewLabel>
                    {preview.trim() ? (
                        <PreviewValue $variant={variant}>{preview}</PreviewValue>
                    ) : (
                        <PreviewEmpty>
                            (empty - pick at least one part)
                        </PreviewEmpty>
                    )}
                    {isPdf && (
                        <PreviewNote>
                            <code>_a{sample.auditId}</code> is the audit-id
                            uniqueness suffix added by the server.{' '}
                            <code>.pdf</code> extension added automatically.
                        </PreviewNote>
                    )}
                </PreviewBox>
            </Modal.Content>
            <Modal.Actions>
                <AppSecondaryButton onClick={onClose}>Cancel</AppSecondaryButton>
                <AppPrimaryButton
                    loading={loading}
                    disabled={!canSave}
                    onClick={handleSave}
                >
                    {saveLabel}
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}
